// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { groupBy } from '@shared/ui-kit/utils';
import produce from 'immer';
import {
  AdvisoryBankProductType,
  type AdvisoryBank,
  type AdvisoryBankProduct,
  type AdvisoryInstitution,
} from '@shared/data-access/types/brokerage-types';
import { getAction as getErrorAction } from './error-codes';
import { generateAssignmentId } from '@shared/data-access/slices/tags-management';
import { TagObjectType } from '@shared/data-access/interfaces/redux/tags-management-state-types';

const sumBalances = (products: { balance: number }[]): number =>
  products.reduce((sum, product) => sum + product.balance, 0);

const getProductBalance = (product: AdvisoryBankProduct): number => {
  switch (product.product_type) {
    case AdvisoryBankProductType.Account:
      return (product.cash ?? 0) + (product.investment ?? 0) - (product.debt ?? 0);
    case AdvisoryBankProductType.Card:
      return -(product.disposed ?? 0);
    case AdvisoryBankProductType.Loan:
      return -(product.pending_amount ?? 0);
    case AdvisoryBankProductType.Portfolio:
      return product.investment ?? 0;
    default:
      return 0;
  }
};

const flattenInstitutionProducts = (institution: AdvisoryInstitution) =>
  [
    ...institution.products.accounts.map(account => ({
      ...account,
      product_type: AdvisoryBankProductType.Account,
    })),
    ...institution.products.cards.map(card => ({
      ...card,
      product_type: AdvisoryBankProductType.Card,
    })),
    ...institution.products.loans.map(loan => ({
      ...loan,
      product_type: AdvisoryBankProductType.Loan,
    })),
    ...institution.products.portfolios.map(portfolio => ({
      ...portfolio,
      product_type: AdvisoryBankProductType.Portfolio,
    })),
  ] as AdvisoryBankProduct[];

export const buildAdvisoryBanks = (
  institutions: AdvisoryInstitution[],
  pendingInstitutions: AdvisoryInstitution[],
): AdvisoryBank[] => {
  const data = [...institutions, ...pendingInstitutions].map(institution => ({
    ...institution,
    balance: 0,
    error: getErrorAction(institution.error ?? null),
    errorMessage: institution.error ?? null,
    products: flattenInstitutionProducts(institution),
  }));

  return produce(data, draft => {
    draft.forEach(institution => {
      const productsByIban = groupBy(institution.products, 'iban');

      const toRemove = new Set<AdvisoryBankProduct>();

      Array.from(productsByIban.entries()).forEach(([, products]) => {
        if (products.length > 1) {
          products.forEach(product => {
            if (
              product.product_type === AdvisoryBankProductType.Account &&
              !product.cash
            ) {
              toRemove.add(product);
            }
          });
        }
      });

      institution.products = institution.products.filter(
        product => !toRemove.has(product),
      );

      const products = institution.products.map(product => ({
        ...product,
        balance: getProductBalance(product),
      })) as AdvisoryBankProduct[];

      institution.products = products;

      institution.balance = sumBalances(products);
    });
  });
};

export interface FilterBanksByTagParams {
  readonly banks: AdvisoryBank[];
  readonly selectedTag: string | undefined;
  readonly tagsByObject: Map<string, { tags: readonly { tag_id: number }[] }>;
}

export const filterBanksByTag = (
  params: FilterBanksByTagParams,
): AdvisoryBank[] => {
  const { banks, selectedTag, tagsByObject } = params;

  const isAll = !selectedTag;

  if (isAll) return banks;

  return banks
    .filter(bank =>
      bank.products.some(product => {
        const tagAssignments = tagsByObject.get(
          generateAssignmentId(TagObjectType.Account, product.iban),
        );

        return tagAssignments?.tags.some(
          tag => tag.tag_id.toString() === selectedTag,
        );
      }),
    )
    .map(bank => {
      const products = bank.products.filter(
        product =>
          tagsByObject
            .get(generateAssignmentId(TagObjectType.Account, product.iban))
            ?.tags.some(tag => tag.tag_id.toString() === selectedTag) ?? false,
      );

      return {
        ...bank,
        products,
        balance: sumBalances(products),
      };
    });
};

export const getAccountsTotals = (
  accounts: AdvisoryBankProduct[],
): { investment: number; liquidity: number; debt: number; cash: number } =>
  accounts.reduce(
    (acc, product) =>
      produce(acc, draft => {
        switch (product.product_type) {
          case AdvisoryBankProductType.Account:
            draft.cash += product.cash ?? 0;
            draft.liquidity += product.available ?? 0;
            draft.debt += product.debt ?? 0;
            draft.investment += product.investment ?? 0;
            break;
          case AdvisoryBankProductType.Portfolio:
            draft.investment += product.investment ?? 0;
            break;
          case AdvisoryBankProductType.Loan:
            draft.debt += product.pending_amount ?? 0;
            break;
          default:
            break;
        }
      }),
    { investment: 0, liquidity: 0, debt: 0, cash: 0 },
  );
